import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton } from '@mui/material';
import React from 'react';
import { useQueryParams } from '../../../../providers/QueryParamsProvider';
import { useTableState } from '../../providers/TableStateProvider';

const PageSelector = () => {
  const tableState = useTableState();
  const queryParams = useQueryParams();
  const [page, setPage] = React.useState<number>(
    Number(queryParams.query?.page ?? 0) + 1
  );

  const handlePageChange = (pageOffset: number) => {
    const nextPage = page + pageOffset;

    const newQueryParams: any = { ...queryParams.query, page: nextPage - 1 };

    // If there is a next or previous token (ElasticSearch query), use it to paginate
    if (tableState.data?.meta) {
      if (pageOffset > 0 && tableState.data.meta.nextToken) {
        newQueryParams.searchAfter = tableState.data.meta.nextToken;
        newQueryParams.pageRequested = 'NEXT';
      } else if (pageOffset < 0 && tableState.data.meta.prevToken) {
        newQueryParams.searchAfter = tableState.data.meta.prevToken;
        newQueryParams.pageRequested = 'PREV';
      }
    }

    queryParams.push({}, { query: newQueryParams });
    setPage(nextPage);
  };

  React.useEffect(() => {
    if (page !== queryParams.query.page) {
      setPage(Number(queryParams.query.page ?? 0) + 1);
    }
  }, [page, queryParams.query.page]);

  const totalPages = React.useMemo(() => {
    if (tableState.data?.totalPages == null) {
      return [];
    }

    const nextPage = page < tableState.data?.totalPages ? page + 1 : null;
    const prevPage = page > 1 ? page - 1 : null;

    return [prevPage, page, nextPage];
  }, [page, tableState.data?.totalPages]);

  return (
    <Box
      position="absolute"
      left="50%"
      top="50%"
      sx={{ transform: 'translate(-50%, -50%)' }}
      display="flex"
      alignItems="center"
      gap="1.25rem"
    >
      <IconButton
        size="small"
        onClick={() => handlePageChange(-1)}
        disabled={page == 1}
      >
        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '1rem' }} />
      </IconButton>
      {totalPages.map((pageNumber) => {
        if (!pageNumber) {
          return null;
        }

        return (
          <IconButton
            key={pageNumber}
            size="small"
            onClick={() => handlePageChange(pageNumber - page)}
            sx={{
              width: 25,
              height: 25,
              borderRadius: 100,
              opacity: 0.8,
              ...(page === pageNumber && {
                backgroundColor: (theme) =>
                  theme.serviceTable.pagination.bgColor,
              }),
              ':hover': {
                backgroundColor: (theme) => theme.serviceTable.pagination.hover,
              },
            }}
          >
            <Box
              className="contrast-text"
              sx={{
                fontSize: '1.25rem',
                color: (theme) => theme.serviceTable.pagination.color,
              }}
            >
              {pageNumber}
            </Box>
          </IconButton>
        );
      })}
      <IconButton
        size="small"
        onClick={() => handlePageChange(1)}
        disabled={page >= (tableState.data?.totalPages ?? 0)}
      >
        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '1rem' }} />
      </IconButton>
    </Box>
  );
};

export default PageSelector;
